<script setup lang="ts">
import type { TabsEmit, TabsProps } from '/#/components/base/tabs'

import { KeepAlive } from 'vue'

import { filterArrayWithShow } from '/@/utils/array'

const emit = defineEmits<TabsEmit>()

const props = withDefaults(defineProps<TabsProps>(), {
  type: undefined,
  align: undefined,
  navigationComponent: 'a',
  useKeepAlive: true,
  theme: 'light',
})

/**
 * @var activeValue {Ref<string>}
 * @description Holds active tab value.
 */
const activeValue: WritableComputedRef<Undefinable<string>> = computed({
  get: () => props.selected,
  set: (value) => emit('update:selected', value as string),
})

/**
 * Tabs were filtered according to whether the show parameter was present or not. If there is no show parameter,
 * the value of true has been added, and if the show value is false, it has been set to not be shown.
 */
const filteredTabs = computed(() => filterArrayWithShow(props.tabs))

/**
 * @method changeActiveTab
 * @param value {string} Holds active tab value.
 * @description Change active tab.
 */
function changeActiveTab(value: string) {
  activeValue.value = value
}

defineExpose({
  changeActiveTab,
  activeValue,
})
</script>

<template>
  <div :class="[theme]">
    <div class="tab-items" :class="[noPadding && '!p-0']">
      <div v-for="tab in filteredTabs" :key="tab.value">
        <Tab
          :key="tab.value"
          :label="tab.label"
          :value="tab.value"
          :icon="tab.icon"
          :badge="tab.badge"
          :tooltip="tab.tooltip"
          :is-active="activeValue === tab.value"
          :class="{
            'is-active': activeValue === tab.value,
            'is-disabled': tab.disabled,
          }"
         
          @click="changeActiveTab(tab.value)"
        />
      </div>
    </div>
    <Divider v-if="divider" />
    <div
      v-if="!hideContent"
      class="tab-content"
     
    >
      <Component :is="useKeepAlive ? KeepAlive : 'span'">
        <slot
          :key="!useKeepAlive && { activeValue }"
          name="tab"
          v-bind="{
            activeValue,
          }"
        />
      </Component>
    </div>
  </div>
</template>
